import { useEffect, useRef, useState } from 'react'
import { Button, Callout, Card, CardContent, CardHeader, DataTable, LoadingState } from './ui'
import { newIdempotencyKey } from '../api/client'
import { documentsApi } from '../api/documents'
import { generatedDocumentsApi } from '../api/documentTemplates'
import type { DocumentPreview, GeneratedDocument, TemplateOption } from '../api/documentTemplates'

/**
 * F-071 (TASK-0325): "Documenti generati" di una pratica. Per ogni tipo di documento con un modello
 * valido mostra quale modello verra' usato (il piu' specifico: prodotto, operatore, settore, generico),
 * permette l'anteprima in un iframe isolato (`sandbox=""`, nessuno script) e la generazione. Il file
 * prodotto e' HTML stampabile (non PDF) e conserva codice e versione del modello; il download passa dal
 * normale `/api/documents/{id}/content`. Mai importi ne' IBAN: il server non li mette fra i dati.
 */
const ROME = 'Europe/Rome'

function formatRome(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone: ROME,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function ContractGeneratedDocuments({ contractId }: { contractId: number }) {
  const [documents, setDocuments] = useState<GeneratedDocument[]>([])
  const [options, setOptions] = useState<TemplateOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<{ kind: string; data: DocumentPreview } | null>(null)
  const [busyKind, setBusyKind] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  // Una chiave per "tentativo" di generazione di un tipo: rigenerata solo dopo un esito.
  const keys = useRef(new Map<string, string>())

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const [docs, opts] = await Promise.all([
        generatedDocumentsApi.list(contractId),
        generatedDocumentsApi.templates(contractId),
      ])
      setDocuments(docs)
      setOptions(opts)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento dei documenti generati')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!Number.isFinite(contractId)) return
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId])

  async function openPreview(kind: string) {
    setBusyKind(kind)
    setError(null)
    setMessage(null)
    try {
      setPreview({ kind, data: await generatedDocumentsApi.preview(contractId, { kind }) })
    } catch (e) {
      setPreview(null)
      setError(e instanceof Error ? e.message : 'Errore nell\'anteprima')
    } finally {
      setBusyKind(null)
    }
  }

  async function generate(kind: string) {
    let key = keys.current.get(kind)
    if (!key) {
      key = newIdempotencyKey()
      keys.current.set(kind, key)
    }
    setBusyKind(kind)
    setError(null)
    setMessage(null)
    try {
      const doc = await generatedDocumentsApi.generate(contractId, kind, key)
      keys.current.delete(kind)
      setMessage(`Documento generato: ${doc.name}`)
      setPreview(null)
      await load()
    } catch (e) {
      // Errore del server (dati mancanti, nessun modello, ...): un nuovo tentativo e' un'altra operazione.
      // Un errore di rete (nessuna risposta) invece riusa la stessa chiave: il server non duplica.
      if (!(e instanceof TypeError)) keys.current.delete(kind)
      setError(e instanceof Error ? e.message : 'Errore nella generazione')
    } finally {
      setBusyKind(null)
    }
  }

  return (
    <Card variant="outlined" className="mt-3">
      <CardHeader icon="description" title="Documenti generati" subtitle="HTML stampabile, non PDF" />
      <CardContent>
        {error && <Callout tone="error">{error}</Callout>}
        {message && <Callout tone="success">{message}</Callout>}
        {loading ? (
          <LoadingState label="Caricamento..." />
        ) : (
          <>
            {options.length === 0 ? (
              <p className="text-muted">Nessun modello attivo si applica a questa pratica.</p>
            ) : (
              <div className="mb-3">
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Modello</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {options.map(o => (
                      <tr key={o.kind}>
                        <td className="md-typescale-emphasized-body-medium">{o.kind}</td>
                        <td className="md-typescale-body-small">
                          {o.outcome === 'selected' && o.template
                            ? `${o.template.name} (${o.template.code} v${o.template.version})`
                            : `Ambiguo: ${o.candidates.map(c => `${c.code} v${c.version}`).join(', ')} — correggi i modelli in Impostazioni`}
                        </td>
                        <td className="text-end">
                          <Button
                            type="button"
                            variant="outlined"
                            size="sm"
                            className="me-1"
                            disabled={o.outcome !== 'selected' || busyKind !== null}
                            onClick={() => void openPreview(o.kind)}
                          >
                            Anteprima
                          </Button>
                          <Button
                            type="button"
                            variant="tonal"
                            size="sm"
                            disabled={o.outcome !== 'selected' || busyKind !== null}
                            onClick={() => void generate(o.kind)}
                          >
                            {busyKind === o.kind ? 'Attendi...' : 'Genera'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </div>
            )}

            {preview && (
              <Card as="div" variant="outlined" padding="none" className="mb-3">
                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                  <span className="md-typescale-body-small">
                    Anteprima: <strong>{preview.kind}</strong> — {preview.data.template.code} v{preview.data.template.version}
                  </span>
                  <Button type="button" variant="text" size="sm" onClick={() => setPreview(null)}>
                    Chiudi
                  </Button>
                </div>
                {preview.data.missing.length > 0 && (
                  <Callout tone="warning" className="m-2">
                    Dati obbligatori mancanti, la generazione sara' rifiutata:{' '}
                    {preview.data.missing.map(m => m.label).join(', ')}.
                  </Callout>
                )}
                <iframe
                  title={`Anteprima ${preview.kind}`}
                  sandbox=""
                  srcDoc={preview.data.html}
                  className="md-u-paper-frame md-u-paper-frame--420"
                />
              </Card>
            )}

            {documents.length === 0 ? (
              <p className="text-muted md-typescale-body-small mb-0">Nessun documento generato per questa pratica.</p>
            ) : (
              <div>
                <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Modello</th>
                      <th>Generato il</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map(d => (
                      <tr key={d.id}>
                        <td>{d.kind ?? d.name}</td>
                        <td className="md-typescale-body-small">{d.templateCode} v{d.templateVersion}</td>
                        <td className="md-typescale-body-small">{formatRome(d.createdAt)}</td>
                        <td className="text-end">
                          <a
                            className="md-button md-button--outlined md-tone--primary md-button--sm md-state md-touch"
                            href={documentsApi.contentUrl(d.id)}
                          >
                            Scarica
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
